// frontend/src/pages/Products.jsx - COMPLETELY CLEANED
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getProducts } from "../api";
import { useAuth } from "../context/AuthContext";
import ProductCard from "../components/Product/ProductCard";
import Loader from "../components/UI/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import {
  faSeedling,
  faFilter,
  faSearch,
  faTimes,
  faTractor,
  faMoneyBillWave,
  faCalendarCheck,
  faCheckCircle,
  faBox,
  faTag,
  faStar,
  faClock,
  faArrowRight,
  faExclamationTriangle,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import SEO from "../components/SEO/SEO";

// ============================================
// CONSTANTS
// ============================================
const CATEGORIES = [
  { value: "", label: "All Products", icon: faBox },
  {
    value: "rumen-bypass-fat",
    label: "Rumen Bypass Fat (GrowPro™)",
    icon: faTag,
  },
  { value: "mineral-blocks", label: "Mineral Blocks", icon: faStar },
  {
    value: "protein-concentrates",
    label: "Protein Concentrates",
    icon: faSeedling,
  },
  { value: "feed-supplements", label: "Feed Supplements", icon: faCheckCircle },
];

// ============================================
// MAIN COMPONENT
// ============================================
const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: searchParams.get("category") || "",
    search: searchParams.get("search") || "",
  });
  const [searchInput, setSearchInput] = useState(filters.search);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const userType = user?.userType || "customer";
  const farmSize = user?.farmDetails?.farmSize || "small";
  const isFarmer = userType === "farmer";

  // ============================================
  // FETCH PRODUCTS
  // ============================================
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getProducts({
          ...filters,
          userType,
          farmSize,
          limit: 50,
        });
        const data = response.data;
        setProducts(data.products || []);
      } catch (error) {
        console.error("Error fetching products:", error);
        setError("Failed to load products. Please try again.");
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [filters, userType, farmSize]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchInput });
    if (searchInput) {
      searchParams.set("search", searchInput);
    } else {
      searchParams.delete("search");
    }
    setSearchParams(searchParams);
  };

  const clearSearch = () => {
    setSearchInput("");
    setFilters({ ...filters, search: "" });
    searchParams.delete("search");
    setSearchParams(searchParams);
  };

  const handleCategoryFilter = (category) => {
    setFilters({ ...filters, category });
    if (category) {
      searchParams.set("category", category);
    } else {
      searchParams.delete("category");
    }
    setSearchParams(searchParams);
  };

  const clearAllFilters = () => {
    setFilters({ category: "", search: "" });
    setSearchInput("");
    searchParams.delete("category");
    searchParams.delete("search");
    setSearchParams(searchParams);
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) return <Loader />;

  return (
    <>
      <SEO
        title="Products - Premium Livestock Feed & Animal Nutrition"
        description="Browse our premium livestock feed supplements and animal nutrition products. Quality feed for cattle, poultry, goats, sheep, and pigs. Globally sourced quality."
        keywords="livestock feed products, animal nutrition supplements, cattle feed, poultry feed, goat feed, sheep feed, Kenali Enterprise"
        canonical="https://kenalienterprise.com/products"
      />
      <motion.div
        className="products-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Hero Section */}
        <section className="products-hero">
          <div className="products-hero-overlay" />
          <div className="container">
            <motion.div
              className="products-hero-content"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <FontAwesomeIcon icon={faSeedling} className="hero-icon" />
              <h1>Our Products</h1>
              <p>
                Premium livestock nutrition solutions for Sierra Leone farmers
              </p>
              <div className="hero-breadcrumb">
                <Link to="/">Home</Link>
                <span>/</span>
                <span>Products</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <div className="container products-content">
          {/* Pre-Order CTA */}
          <motion.div
            className="preorder-cta"
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <div className="preorder-cta-content">
              <FontAwesomeIcon icon={faCalendarCheck} />
              <div>
                <strong>Pre-Order Kenali GrowPro™</strong>
                <span>
                  50% deposit now, 50% on pickup. Limited stock available.
                </span>
              </div>
            </div>
            <Link to="/preorder" className="preorder-cta-btn">
              Reserve Now <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </motion.div>

          {/* Error Notice */}
          {error && (
            <motion.div
              className="error-notice"
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{error}</span>
            </motion.div>
          )}

          {/* Farmer Notice */}
          {isFarmer && (
            <motion.div
              className="farmer-notice"
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <FontAwesomeIcon icon={faTractor} />
              <div>
                <strong>Farmer Bulk Pricing Active!</strong>
                <span>
                  Wholesale and bulk prices available. Minimum order: 25kg per
                  product.
                </span>
              </div>
            </motion.div>
          )}

          {/* Payment Notice */}
          <motion.div
            className="payment-notice"
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <FontAwesomeIcon icon={faMoneyBillWave} />
            <span>
              <strong>Flexible Payments:</strong> Cash on Delivery • Orange
              Money • Afrimoney • WAVE • Bank Transfer
            </span>
            <a
              href="https://wa.me/23231030808"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-link"
            >
              <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp Us
            </a>
          </motion.div>

          {/* Search & Filter Section */}
          <div className="search-filter-section">
            <div className="search-row">
              <form onSubmit={handleSearch} className="search-form">
                <div className="search-input-wrapper">
                  <FontAwesomeIcon icon={faSearch} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="search-input"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="clear-search"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
                <button type="submit" className="search-btn">
                  <FontAwesomeIcon icon={faSearch} /> Search
                </button>
              </form>

              <button
                className="filter-toggle"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
              >
                <FontAwesomeIcon icon={faFilter} />
                <span>Filter</span>
              </button>
            </div>

            <div className={`filter-chips ${isFilterOpen ? "open" : ""}`}>
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => handleCategoryFilter(cat.value)}
                  className={`filter-chip ${filters.category === cat.value ? "active" : ""}`}
                >
                  <FontAwesomeIcon icon={cat.icon} />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Results Header */}
          <div className="results-header">
            <span>Showing {products.length} products</span>
            {isFarmer && (
              <span className="bulk-badge">
                <FontAwesomeIcon icon={faTractor} /> Bulk pricing applied
              </span>
            )}
            {filters.category && (
              <button onClick={clearAllFilters} className="clear-filters">
                <FontAwesomeIcon icon={faTimes} /> Clear Filters
              </button>
            )}
          </div>

          {/* Products Grid */}
          {products.length > 0 ? (
            <div className="products-grid">
              {products.map((product, index) => (
                <motion.div
                  key={product._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <ProductCard
                    product={product}
                    userType={userType}
                    farmSize={farmSize}
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3>No products found</h3>
              <p>Try adjusting your search or filter criteria.</p>
              <button onClick={clearAllFilters} className="clear-filters-btn">
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default Products;
